import { TEDCApplicant, TQuestionWithAnswer, useEDCFlow, VALIDATION_STATUS, ValidationResult } from "@smvtech/x-flux";
import { useEffect, useMemo, useState } from "react";
import { MIXPANEL_EVENTS } from "constants/mixpanelEvents";
import { useEDCFlowContext } from "pages/EDCFlow/context";
import { isPdfFile } from "pages/EDCFlow/helper";
import Mixpanel from "utils/mixpanel";

type UseDocumentItemParams = {
	document: TQuestionWithAnswer;
	activeApplicantData: TEDCApplicant;
	questions: TQuestionWithAnswer[];
	validationResult: ValidationResult;
};

export const useDocumentItem = ({
	document,
	activeApplicantData,
	questions,
	validationResult
}: UseDocumentItemParams) => {
	const { getS3UrlForAsset, updateAnswer } = useEDCFlow();
	const { setTncAccepted } = useEDCFlowContext();

	const [signedUrl, setSignedUrl] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [uploading, setUploading] = useState<{ isUploading: boolean; progress: number }>({
		isUploading: false,
		progress: 0
	});
	const [uploadFailed, setUploadFailed] = useState(false);
	const [showModalWithSignedURL, setShowModalWithSignedURL] = useState<{
		signedURL: string;
		isPDF: boolean;
	} | null>(null);

	const assets = document?.answer_data?.assets_id;
	const approvedAsset = assets?.find((asset) => asset.status === "APPROVED");
	const assetId = approvedAsset?.doc_id;
	const hasDocument = !!signedUrl;

	const reUploadInputId = `reupload-file-input-${document.id}-${activeApplicantData.application_id}`;

	const timeSaved = useMemo(() => {
		const secondsSaved = Math.max(questions?.length * 30, 120);
		return secondsSaved;
	}, [questions.length]);

	useEffect(() => {
		if (!assetId) {
			setSignedUrl(null);
			return;
		}

		setIsLoading(true);
		getS3UrlForAsset(assetId)
			.then((url) => setSignedUrl(url))
			.finally(() => setIsLoading(false));
	}, [assetId, getS3UrlForAsset]);

	const handleUploadStart = () => {
		setUploadFailed(false);
		setUploading({ isUploading: true, progress: 0 });
		setSignedUrl(null);
	};

	const handleUploadProgress = (progress: number) => {
		setUploading({ isUploading: true, progress });
	};

	const handleUploadComplete = () => {
		setUploading({ isUploading: false, progress: 0 });
	};

	const handleReUpload = async (files: FileList | null) => {
		if (!files || files.length === 0) return;

		const file = files[0];
		handleUploadStart();

		try {
			await updateAnswer(
				activeApplicantData.application_id,
				document.question.type,
				document.question.question,
				{
					visa_question_id: document.id,
					answer: file.name,
					traveller_id: activeApplicantData.traveller._id,
					file
				},
				(event) => {
					handleUploadProgress(event.percent || 0);
				}
			);

			Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.DOCUMENT_UPLOADED, {
				applicationId: activeApplicantData.application_id,
				documentId: document.id,
				documentType: document.question.doc_type,
				document: document.question.question,
				reupload: true
			});
			handleUploadComplete();
		} catch (error) {
			console.error("Error re-uploading document:", error);
			setUploadFailed(true);
			handleUploadComplete();
		} finally {
			setTncAccepted(false);
		}
	};

	const isPdf = useMemo(
		() => isPdfFile(signedUrl, document.answer_data?.answer?.[0]),
		[signedUrl, document.answer_data?.answer]
	);

	const handlePreviewClick = (url: string, isPdf: boolean) => {
		if (isPdf && url) {
			setShowModalWithSignedURL({ signedURL: url, isPDF: isPdf });
		}
	};

	const isUnfixable = validationResult?.status === VALIDATION_STATUS.UNFIXABLE;
	const isVerified =
		validationResult?.status === VALIDATION_STATUS.PERFECT ||
		validationResult?.status === VALIDATION_STATUS.FIXABLE;

	return {
		signedUrl,
		isLoading,
		uploading,
		uploadFailed,
		hasDocument,
		isPdf,
		reUploadInputId,
		timeSaved,
		showModalWithSignedURL,
		setShowModalWithSignedURL,
		handleUploadStart,
		handleUploadProgress,
		handleUploadComplete,
		handleReUpload,
		handlePreviewClick,
		isUnfixable,
		isVerified
	};
};

